import type { ProcoreAssigneeResolution } from "../../../../shared/procoreDailyLog";

/**
 * Which of the mutually exclusive assignee affordances a surface must render.
 *
 * Exactly one is ever active. The original bug was that three surfaces each
 * inferred this from their own combination of `resolution.state`,
 * `distribution.length` and the raw assignee text, and disagreed — the review
 * cards suppressed unmatched prose while the detail views locked a pill around
 * it. Every surface now reads this one value.
 *
 *   • locked         — a directory person is chosen. Show `name` in the bubble.
 *   • suggesting     — candidates exist, none chosen. Show the ghost face-stack.
 *   • unmatched_text — the spoken assignee is a specific party that is NOT in
 *                      the Procore directory, so it belongs in the description
 *                      rather than the assignee field. NOT EMITTED YET: it needs
 *                      an extraction-time classifier to tell "John Reyes from
 *                      Woodworks" (specific) from "the architect" (a bare role).
 *                      Until that lands these cases fall to `empty`.
 *   • empty          — nothing to show. Plain "Unassigned" + directory search.
 */
export type AssigneeDisplayMode =
  | "locked"
  | "suggesting"
  | "unmatched_text"
  | "empty";

export interface AssigneeDisplay {
  mode: AssigneeDisplayMode;
  /** The name to lock onto. Non-empty only when mode is "locked". */
  name: string;
}

/**
 * Resolves the single active assignee affordance for a record.
 *
 * With Procore connected the assignee must be a real directory person — either
 * a resolved candidate (auto/manual) or an explicitly committed directory id.
 * The raw AI-extracted text ("Architect", "the electrician", "John from
 * Woodworks") is only a matching hint and must NEVER lock the field.
 */
export function assigneeDisplay(opts: {
  resolution?: ProcoreAssigneeResolution | null;
  assignee: string;
  selectedRemoteId?: string | null;
  /**
   * False when the record's project has no Procore link — free text is the only
   * option there, so it legitimately locks the field.
   */
  procoreConnected?: boolean;
  /** Post-acceptance editing suppresses `suggesting` (directory search only). */
  editMode?: boolean;
}): AssigneeDisplay {
  const candidate = opts.resolution?.selectedCandidate;
  const trimmedAssignee = (opts.assignee ?? "").trim();
  const committedRemoteId = (opts.selectedRemoteId ?? "").trim();
  const procoreConnected = opts.procoreConnected ?? true;

  // A resolved candidate the draft hasn't been pointed away from — show the
  // resolved Procore name. Requires non-empty assignee so an explicit clear
  // (empty assignee + unset id) does NOT fall back to the auto-pick.
  const showResolvedName =
    !!candidate &&
    trimmedAssignee.length > 0 &&
    (!committedRemoteId ||
      committedRemoteId === candidate.remoteId ||
      committedRemoteId === candidate.projectUserRemoteId);
  if (showResolvedName) {
    return { mode: "locked", name: candidate.name };
  }

  // An explicit directory selection (manual pick, or an auto-resolution that
  // committed a login-information id) locks onto its name.
  if (committedRemoteId.length > 0 && trimmedAssignee.length > 0) {
    return { mode: "locked", name: trimmedAssignee };
  }

  // Without Procore there is no directory to match against, so the typed text
  // IS the assignee.
  if (!procoreConnected && trimmedAssignee.length > 0) {
    return { mode: "locked", name: trimmedAssignee };
  }

  // Unlocked. Cards win when the resolution carries any; otherwise the field is
  // empty. Note this deliberately keys on `topCandidates` — the same list the
  // ghost stack renders — so a resolution that claims `suggest_distribution` but
  // carries no displayable candidate (every match fell under the distribution
  // score floor) lands on `empty` with a working directory search, rather than
  // the silent dead row where neither affordance appeared.
  const candidates = opts.editMode ? [] : (opts.resolution?.topCandidates ?? []);
  if (candidates.length > 0) {
    return { mode: "suggesting", name: "" };
  }

  return { mode: "empty", name: "" };
}

/**
 * The name to show in the AssigneeField bubble, or "" to leave it unlocked.
 *
 * Thin wrapper over {@link assigneeDisplay} for callers that only need the
 * bubble text.
 */
export function reviewAssigneeDisplayName(opts: {
  resolution?: ProcoreAssigneeResolution | null;
  assignee: string;
  selectedRemoteId?: string | null;
  procoreConnected?: boolean;
  editMode?: boolean;
}): string {
  return assigneeDisplay(opts).name;
}

/**
 * Banner copy for the Assign-to page, keyed on the resolution state.
 *
 * A committed pick (manual OR auto) must never read as "No clear match" —
 * that copy is only for genuine ambiguity. The trade→commitment framing is
 * kept for ambiguous distributions that at least matched a vendor.
 */
export function assignToBannerCopy(
  resolution: ProcoreAssigneeResolution | null | undefined,
): { title: string; sub: string } {
  if (resolution?.state === "auto_selected") {
    return {
      title: "Likely match",
      sub: "High confidence in one person — pick to assign.",
    };
  }

  if (resolution?.state === "manual_selected") {
    const name = resolution.selectedCandidate?.name?.trim();
    return {
      title: "Assigned",
      sub: name
        ? `${name} is set — pick someone else to reassign.`
        : "An assignee is set — pick someone else to reassign.",
    };
  }

  const vendor = resolution?.matchedVendor;
  if (vendor?.tradeName) {
    return {
      title: `${vendor.tradeName} → commitment`,
      sub: `${vendor.label} · ranked by assignment frequency`,
    };
  }

  return {
    title: "No clear match",
    sub: "Several people fit what was discussed — browse and pick one.",
  };
}

/** Which company directory a heard name was checked against. */
export type CompanyDirectoryProvider = "procore" | "autodesk" | "files";

function companyDirectoryName(provider: CompanyDirectoryProvider): { brand: string; directory: string } {
  if (provider === "autodesk") return { brand: "Autodesk", directory: "the Autodesk directory" };
  if (provider === "files") return { brand: "the company list", directory: "the company list" };
  return { brand: "Procore", directory: "the Procore directory" };
}

/**
 * What a heard Company's record lets the review copy CLAIM about the directory
 * (LED-724). One answer for the chip and the picker banner, so they never
 * disagree:
 *
 *  - `not_in_directory`: the directory was checked and holds no close match.
 *    That includes a WEAK tie (`close_competition` only says the top two were
 *    within the margin, at any score): "Red Electrical" vs ABC 50 / Bright 48.
 *  - `directory_unavailable`: there were no directory companies to compare
 *    with at processing (`empty_directory`): the lookup failed, or the
 *    directory was genuinely empty. Nobody checked, so "not in it" is a claim
 *    nobody made.
 *  - `matches_several`: the heard name matched MORE THAN ONE directory company
 *    about equally well (`heardMatchesSeveral`, written by the matcher), e.g.
 *    "W&J HVAC Solutions" held with and without "LLC". It is in the directory,
 *    more than once, and the manpower merge, which matches a heard name to a
 *    Procore log by its normalized name, can show that company's record on
 *    the same card.
 */
export type HeardCompanyStanding = "not_in_directory" | "directory_unavailable" | "matches_several";

export function heardCompanyStanding(
  resolution: ProcoreAssigneeResolution | null | undefined,
): HeardCompanyStanding {
  if (resolution?.reason === "empty_directory") return "directory_unavailable";
  if (resolution?.heardMatchesSeveral === true) return "matches_several";
  return "not_in_directory";
}

/**
 * The company picker's banner when the walk NAMED a company the matcher could
 * not resolve (LED-724): the record keeps the heard name, unresolved.
 *
 * Says what was heard and where it stands against the directory (see
 * `HeardCompanyStanding`), then what to do. Never the person-flavoured
 * `assignToBannerCopy` fallback: "Several people fit what was discussed" was
 * wrong twice for a company nobody in the directory is. For an unavailable
 * directory the copy says only what is true whether the lookup failed or the
 * directory was empty ("couldn't be checked" would be wrong for the second).
 */
export function heardCompanyCopy(opts: {
  heard: string;
  provider: CompanyDirectoryProvider;
  hasCandidates: boolean;
  standing?: HeardCompanyStanding;
}): { title: string; sub: string } {
  const { brand, directory } = companyDirectoryName(opts.provider);
  const standing = opts.standing ?? "not_in_directory";
  const heard = `“${opts.heard}”`;
  // Only a company chosen from the directory is ever sent as the company.
  const consequence =
    opts.provider === "files"
      ? "Only a company picked from the list is saved as the company."
      : `${brand} only gets a company picked from its directory.`;
  if (standing === "directory_unavailable") {
    return {
      title: opts.provider === "files" ? "Not matched to the company list" : `Not matched to ${directory}`,
      sub: `Heard ${heard} on the walk — no companies from ${directory} were available to check it against when the walk was processed. Search for the company. ${consequence}`,
    };
  }
  const where =
    opts.provider === "files" ? "the company list" : `the ${brand} company directory`;
  if (standing === "matches_several") {
    return {
      title: opts.provider === "files" ? "Matches more than one company in the list" : `Matches more than one ${brand} company`,
      sub: `Heard ${heard} on the walk — it matches more than one company in ${where}. Pick the one it should be. ${consequence}`,
    };
  }
  return {
    title: opts.provider === "files" ? "Not in the company list" : `Not in ${directory}`,
    sub: opts.hasCandidates
      ? `Heard ${heard} on the walk — it isn't in ${where}. Pick the company it should be, or search for another. ${consequence}`
      : `Heard ${heard} on the walk — it isn't in ${where}. Search for the company it should be. ${consequence}`,
  };
}

/**
 * The Company chip's unresolved note (LED-724): a short visible word for the
 * chip, and the full phrase for its accessible label.
 *
 * It says what the picker banner says (`HeardCompanyStanding`). "Not in" the
 * directory only when the directory was checked and holds no close match;
 * "Not matched" otherwise: to a directory nobody could check, or because the
 * name matches more than one company in it. Those two are also the only cases
 * in which the manpower merge (which matches a heard name to a Procore log by
 * its normalized name) can show that company's Procore record on the same
 * card, so "Not in Procore" there would contradict the card itself.
 */
export function heardCompanyChipNote(
  provider: CompanyDirectoryProvider,
  standing: HeardCompanyStanding = "not_in_directory",
): { short: string; full: string } {
  const { brand, directory } = companyDirectoryName(provider);
  if (standing === "directory_unavailable") return { short: "Not matched", full: `not matched to ${directory}` };
  if (standing === "matches_several") return { short: "Not matched", full: `matches more than one company in ${directory}` };
  if (provider === "files") return { short: "Not in list", full: "not in the company list" };
  return { short: `Not in ${brand}`, full: `not in ${directory}` };
}
